import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import { ArrowRight, CheckCircle, CheckCircle2 } from 'lucide-react'
import Banner from '../../Components/Banner';

import { Helmet } from 'react-helmet-async';
import StaffAugmentationSlider from './ai/StaffAugmentationSlider';
import ContactSection from '../../Components/home/ContactSection';
const featuresData = [
    {
        id: 1,
        title: "Scale on Demand",
        description:
            "Easily scale your team up or down as your project changes, get the right talent when you need it, without any long-term hiring pressure.",
        image: "/assets/v/item1.png",
    },
    {
        id: 2,
        title: "Access Specialized Skills",
        description:
            "Get the right niche experts instantly, fill skill gaps quickly, and grow your team without the need for lengthy training.",
        image: "/assets/v/item4.png",
    },
    {
        id: 3,
        title: "Reduce Overhead",
        description:
            "Reduce recruitment, HR, payroll, and operational costs to run more efficiently.",
        image: "/assets/v/item13.jpg",
    },
    {
        id: 4,
        title: "Maintain Control",
        description:
            "Stay in full control of your projects and roadmap while confidently managing and guiding your extended team members.",
        image: "/assets/v/item14.png",
    },
];

const toolsData = [
    {
        id: 1,
        title: "Full Stack Developers",
    },
    {
        id: 2,
        title: "Frontend & Backend Engineers",
    },
    {
        id: 3,
        title: "Mobile App Developers (iOS / Android / Flutter)",
    },
    {
        id: 4,
        title: "AI / ML Engineers",
    },
    {
        id: 5,
        title: "Cloud & DevOps Specialists",
    },
    {
        id: 6,
        title: "QA & Automation Engineers",
    },
    {
        id: 7,
        title: "UI/UX Designers",
    },
    {
        id: 8,
        title: "Technical Project Managers",
    },
];

const outcomesData = [
    {
        id: 1,
        text: "Dedicated Resource Model",
        desc: "Full-time professionals working closely with your team, dedicated to long-term project success and growth.",
    },
    {
        id: 2,
        text: "Time-Based Augmentation",
        desc: "Flexible short-term experts to quickly handle bugs, tasks, and sudden workload spikes efficiently.",
    },
    {
        id: 3,
        text: "Hybrid Teams",
        desc: "Combine your in-house team with Mobikode engineers to accelerate delivery and enable seamless knowledge sharing.",
    },
];
const whyMobikodePoints = [
    "Carefully vetted engineers you can trust",
    "Flexible engagement models tailored to your needs",
    "Transparent and honest pricing, always",
    "Smooth collaboration aligned with your time zone",
    "Strong focus on quality, ownership, and accountability",
    "Proven experience in delivering across global markets",
];

const approachData = [
    {
        id: 1,
        step: "01",
        title: "Requirement Discovery",
        desc: "We take time to truly understand your technical needs, team setup, timelines, and working culture.",
    },
    {
        id: 2,
        step: "02",
        title: "Talent Matching",
        desc: "We handpick engineers who not only have strong technical skills, but also communicate clearly and bring the right attitude to work.",
    },
    {
        id: 3,
        step: "03",
        title: "Seamless Onboarding",
        desc: "Our professionals work closely with your team, fitting right into your tools and processes as if they’ve always been part of it.",
    },
    {
        id: 4,
        step: "04",
        title: "Ongoing Support",
        desc: "We ensure smooth performance, steady continuity, and flexible collaboration throughout.",
    },
];

function StaffAugmentationService() {
    return (
        <div>

            <Helmet>
                <title>
                    IT Staff Augmentation Services | Hire Skilled Tech Teams | Mobikode
                </title>

                <meta
                    name="description"
                    content="Mobikode provides flexible IT staff augmentation services to help businesses quickly scale development teams with skilled software developers, AI/ML engineers, cloud specialists, QA engineers, UI/UX designers, and project managers."
                />

                <meta
                    name="keywords"
                    content="Staff Augmentation Services, IT Staff Augmentation, Staff Augmentation Company, IT Staff Augmentation Services, Software Development Staff Augmentation, Dedicated Development Team, Hire Software Developers, Dedicated Developers, IT Resource Augmentation, Technical Staff Augmentation, Developer Staff Augmentation, Remote Development Team, Dedicated IT Team, AI ML Engineers, Cloud DevOps Specialists, QA Automation Engineers, UI UX Designers, Technical Project Managers, Flexible IT Staffing"
                />

                <link
                    rel="canonical"
                    href="https://www.mobikode.com/staff-augmentation"
                />

                <meta
                    property="og:title"
                    content="IT Staff Augmentation Services | Hire Skilled Tech Teams | Mobikode"
                />

                <meta
                    property="og:description"
                    content="Scale your technology team with Mobikode's staff augmentation services. Access skilled developers, engineers, QA professionals, designers, and technical experts through flexible engagement models."
                />

                <meta
                    property="og:url"
                    content="https://www.mobikode.com/staff-augmentation"
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:image"
                    content="https://www.mobikode.com/assets/v/item19.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="IT Staff Augmentation Services | Hire Skilled Tech Teams | Mobikode"
                />

                <meta
                    name="twitter:description"
                    content="Build and scale your technology team with Mobikode's flexible staff augmentation models, including dedicated resources, time-based experts, and hybrid development teams."
                />

                <meta
                    name="twitter:image"
                    content="https://www.mobikode.com/assets/v/item19.png"
                />
            </Helmet>
            <AiHero title="Staff Augmentation Services" imgData="/assets/v/item19.png" />
            <section className="w-full py-[60px] lg:py-[100px] bg-white overflow-hidden font-sans">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
                    <div className="w-full bg-white border border-[#D5DDE8] flex flex-col lg:flex-row items-stretch">

                        <div className="w-full lg:w-full max-w-[498px] min-h-[260px] md:min-h-[350px] lg:min-h-[420px] overflow-hidden bg-gray-50">
                            <img
                                src="/assets/what/img1.png"
                                alt="ai automation"
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                            />
                        </div>

                        <div className="w-full lg:w-1/2 p-6 sm:p-10 lg:p-14 flex flex-col justify-center text-left">
                            <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-3">
                                Start with the right foundation
                            </p>
                            <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.25] tracking-tight mb-5">
                                Scale Your Team. Stay in Control.
                            </h2>
                            <p className="font-graphik text-[14px] lg:text-[16px] text-[#49577A] leading-[1.7] font-[500]">
                                At Mobikode, our Staff Augmentation service helps you quickly bring in skilled professionals who fit right into your team. Whether you’re moving fast on a project, need extra expertise, or want to scale without the hassle of hiring full-time, we’ve got you covered. You stay focused on your goals,we quietly support your execution in the background.
                            </p>
                        </div>

                    </div>
                </div>
            </section>
            <WhyAIAutomation heading="Why Choose Staff Augmentation?" approachData={featuresData} />
            {/* <TechServicesSection title="Explore Our Services" sData={services} /> */}
            {/* <IndustriesWeServe /> */}
            <StaffAugmentationSlider approachData={approachData} />
            {/* <section className="w-full bg-[#FDFDFD] pb-16 lg:pb-24 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="mb-[30px] lg:mb-[60px]">
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                            Our Staff Augmentation Model
                        </h2>
                        <p className="text-[#49577A] mt-2 text-left font-graphik font-[500] text-[14px] lg:text-[16px]">

                            We don’t just provide resources, we bring in responsible professionals who truly understand and align with your goals.

                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 lg:gap-8">
                        {approachData.map((item) => (
                            <div
                                key={item.id}
                                className="relative bg-[#F0F0F0] p-8 min-h-[180px] flex flex-col justify-center overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2 leading-tight">
                                        {item.title}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                                        {item.desc}
                                    </p>
                                </div>
                                <div
                                    className="absolute bottom-0 right-0 w-[180px] h-[140px] pointer-events-none transition-transform duration-500 group-hover:scale-110 opacity-50 lg:opacity-100"
                                >
                                    <img src="/assets/career/wrap1.svg" alt="" className="w-full h-full object-contain object-bottom-right" />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section> */}
            <section className="w-full bg-white pb-[60px] lg:pb-[90px] relative">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

                    <div className="mb-[36px] lg:mb-[52px]">
                        <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
                            Talent Pool
                        </p>
                        <h2 className="text-[#01386A] font-sora text-[20px] sm:text-[24px] lg:text-[30px] font-[600] leading-[1.3] tracking-tight mb-3">
                            Talent You Can Augment
                        </h2>
                        <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] leading-[1.6] max-w-[680px]">
                            We support startups and enterprises, helping teams grow at every stage.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {toolsData.map((item, index) => {
                            const stepNum = String(index + 1).padStart(2, '0');

                            return (
                                <div
                                    key={item.id || index}
                                    className="relative bg-white border border-[#D9E2EC] p-7 sm:p-9 min-h-[220px] flex flex-col justify-between overflow-hidden group transition-all duration-300 hover:shadow-[0_12px_32px_rgba(1,56,106,0.08)] hover:-translate-y-1"
                                >
                                    <div>
                                        <div className="flex items-center justify-between w-full mb-6">
                                            <span className="font-sora text-[22px] sm:text-[26px] font-[600] text-[#01386A]/20 group-hover:text-[#01386A]/40 transition-colors duration-300 select-none">
                                                {stepNum}
                                            </span>
                                        </div>

                                        <h3 className="text-[#01386A] text-[18px] sm:text-[20px] lg:text-[22px] font-[600] font-sora mb-3 leading-tight tracking-tight">
                                            {item.title}
                                        </h3>

                                        <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] leading-[1.65]">
                                            {item.desc}
                                        </p>
                                    </div>

                                    <div className="absolute bottom-0 left-0 w-full h-[3px] bg-[#01386A]/15" />
                                    <div className="absolute bottom-0 left-0 h-[3px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full" />
                                </div>
                            );
                        })}
                    </div>

                </div>
            </section>
            <section className="relative w-full bg-white pb-[60px] lg:pb-[90px]">
                <div className="relative z-10 mx-auto max-w-[1440px] px-[20px] lg:px-[80px]">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

                        <div className="lg:col-span-5 h-full">
                            <div className="lg:sticky lg:top-28">
                                <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
                                    Consulting capabilities
                                </p>
                                <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.2] tracking-tight mb-5">
                                    Engagement Models
                                </h2>
                                <p className="font-graphik font-[500] text-[14px] lg:text-[16px] text-[#49577A] leading-[1.7] max-w-[440px]">
                                    Flexible collaboration structures built around your delivery milestones, technology stack, and scaling priorities.
                                </p>
                            </div>
                        </div>

                        <div className="lg:col-span-7 flex flex-col gap-5 sm:gap-6">
                            {outcomesData.map((item, index) => {
                                const stepNum = String(index + 1).padStart(2, '0');
                                const gradients = [
                                    "from-[#E8ECF8] via-[#ECEEFA] to-[#D5DCF5]",
                                    "from-[#E2E6EE] via-[#E8EDF4] to-[#C9D4E2]",
                                    "from-[#DFF2EB] via-[#E6F4EF] to-[#BEE6D8]",
                                    "from-[#D8E8F5] via-[#E2F0F9] to-[#BAD8EE]",
                                    "from-[#EAE7F8] via-[#EFEBF9] to-[#D5CFF3]",
                                    "from-[#E7ECF3] via-[#EFF3F8] to-[#CBD8E7]",
                                ];
                                const cardGradient = item.gradient || gradients[index % gradients.length];

                                return (
                                    <div
                                        key={item.id || index}
                                        className={`relative overflow-hidden rounded-sm bg-gradient-to-r ${cardGradient} p-7 sm:p-9 lg:p-10 transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
                                    >
                                        <span className="absolute top-4 right-6 sm:top-6 sm:right-8 font-sora text-[32px] sm:text-[44px] font-[600] text-[#01386A]/10 select-none pointer-events-none">
                                            {item.step || stepNum}
                                        </span>

                                        <div className="relative z-10 max-w-[560px]">
                                            <h3 className="font-sora text-[16px] lg:text-[20px] font-[600] text-[#01386A] tracking-tight leading-[1.3] mb-3">
                                                {item.text || item.title}
                                            </h3>

                                            {item.desc && (
                                                <p className="font-graphik text-[14px]  lg:text-[16px] text-[#2C3E55] leading-[1.65] font-[500] mb-4">
                                                    {item.desc}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </div>
            </section>
            <section className="w-full pb-[20px] bg-white">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
                    <div className="w-full bg-white border border-[#D5DDE8] flex flex-col lg:flex-row items-stretch">

                        <div className="w-full lg:w-1/2 min-h-[300px] sm:min-h-[380px] lg:min-h-[460px] overflow-hidden bg-gray-50">
                            <img
                                src="/assets/what/img6.png"
                                alt="Why Mobikode"
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                            />
                        </div>

                        <div className="w-full lg:w-1/2 p-6 sm:p-10 lg:p-14 flex flex-col justify-center text-left">
                            <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-2.5">
                                Start with the right foundation
                            </p>

                            <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.3] tracking-tight mb-2">
                                Why Mobikode?
                            </h2>

                            <p className="font-graphik text-[14px] lg:text-[16px] text-[#49577A] leading-[1.6] font-[500] mb-6">
                                We seamlessly integrate with your team, working as true partners, not just external vendors.
                            </p>

                            <div className="flex flex-col gap-3.5 mb-6">
                                {whyMobikodePoints.map((point, index) => (
                                    <div key={index} className="flex items-start gap-3 group">
                                        <div className="mt-0.5 shrink-0">
                                            <CheckCircle2 size={18} className="text-[#ED6800]" />
                                        </div>
                                        <p className="text-[#01386A] font-graphik text-[14px] lg:text-[16px] font-[500] leading-relaxed transition-colors duration-200 group-hover:text-[#ED6800]">
                                            {point}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <p className="font-graphik text-[14px] lg:text-[16px] text-[#49577A] leading-[1.6] font-[500] border-t border-[#E6EBEF] pt-4">
                                Partner with us for reliable, scalable, and future-ready engineering solutions that drive productivity and operational excellence.
                            </p>
                        </div>

                    </div>
                </div>
            </section>
            {/* <section className="relative w-full h-[400px] lg:h-[507px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/career/img12.png"
                        alt="Staff Augmentation Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] h-full flex items-center lg:items-center ">
                    <div className="bg-white p-8 lg:p-12 max-w-[600px] shadow-2xl rounded-sm">

                        <h2 className="text-[#01386A] text-[30px] lg:text-[40px] font-[600] font-sora leading-tight mb-6">
                            Staff Augmentation Services
                        </h2>
                        <p className="text-[#49577A] text-[16px] leading-[24px] font-graphik mb-8">
                            Quickly scale your development team with experienced global talent, giving you the flexible, on-demand support needed to move faster and deliver projects on time with confidence.
                        </p>
                        <a href="/contact" className="inline-flex items-center gap-2 text-[#01386A] underline font-graphik font-semibold text-lg hover:gap-3 transition-all duration-300 group">
                            Contact Now
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </a>

                    </div>
                </div>
            </section> */}
            <br/>
            <br/>
            <br/>
            <Banner title="Staff Augmentation Services" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
            <ContactSection/>
        </div>
    )
}

export default StaffAugmentationService
